import commonComponentsMessages from '../../common-components/messages';
import messages from '../../register/messages';
import {
  LOWERCASE_REGEX,
  MINIMUM_PASSWORD_LENGTH,
  NUMBER_REGEX,
  ONLY_ENGLISH_LETTERS,
  SPECIAL_CHARACTERS_REGEX,
  UPPERCASE_REGEX,
} from '../constants';

export const passwordConditions = (value) => ({
  englishLetters: ONLY_ENGLISH_LETTERS.test(value),
  uppercase: UPPERCASE_REGEX.test(value),
  lowercase: LOWERCASE_REGEX.test(value),
  minimumLength: value.length >= MINIMUM_PASSWORD_LENGTH,
  specialCharacters: SPECIAL_CHARACTERS_REGEX.test(value),
  hasNumber: NUMBER_REGEX.test(value),
});

export const countCategorySignals = (conditions) => (
  (conditions.lowercase ? 1 : 0) +
  (conditions.uppercase ? 1 : 0) +
  (conditions.specialCharacters ? 1 : 0) +
  (conditions.hasNumber ? 1 : 0)
);

export const strengthIndexFromScore = (total) => {
  if (total >= 5) return 3;
  if (total === 4) return 2;
  if (total === 3) return 1;
  return 0; 
};

export const getPasswordStrengthIndex = (value) => {
  if (!value) return null;

  const conditions = passwordConditions(value);
  const categorySignals = countCategorySignals(conditions);

  const hasMinLength = conditions.minimumLength;
  const isLong = value.length > MINIMUM_PASSWORD_LENGTH;

  let total = categorySignals + (categorySignals === 4 && isLong ? 1 : 0 );

  if (!hasMinLength) {
    total = Math.max(total - 1, 0);
  }

  return strengthIndexFromScore(total);
};

export const convertConditionsForPasswordStrengthBar = (value) => {
  const conditions = passwordConditions(value);
  return [
    conditions.lowercase,
    conditions.uppercase,
    conditions.specialCharacters,
    conditions.hasNumber,
  ].filter((condition) => condition).length - 1;
};

export const convertConditionsWithMessages = (value, confirmPasswordValue, formatMessage) => {
  const conditions = passwordConditions(value);
  const conditionsMessages = [
    {
      condition: conditions.englishLetters,
      message: formatMessage(commonComponentsMessages['password.strength.only.english.letters.validation']),
    },
    {
      condition: conditions.uppercase,
      message: formatMessage(commonComponentsMessages['password.strength.one.uppercase.validation']),
    },
    {
      condition: conditions.lowercase,
      message: formatMessage(commonComponentsMessages['password.strength.one.lowercase.validation']),
    },
    {
      condition: conditions.minimumLength,
      message: formatMessage(
        commonComponentsMessages['password.strength.minimum.letters.validation'], 
        { minLength: MINIMUM_PASSWORD_LENGTH }
      ),
    },
    {
      condition: conditions.specialCharacters,
      message: formatMessage(commonComponentsMessages['password.strength.at.least.one.special.character.validation']),
    },
    {
      condition: conditions.hasNumber,
      message: formatMessage(commonComponentsMessages['password.strength.at.least.one.number.validation']),
    },
  ];

  if (confirmPasswordValue) {
    conditionsMessages.unshift({
      condition: value === confirmPasswordValue,
      message: formatMessage(commonComponentsMessages['password.strength.not.match.validation']),
    });
  }

  return conditionsMessages;
};

/**
 * It validates the password field value
 * @param value
 * @param formatMessage
 * @returns {string}
 */
export const validatePasswordField = (value, formatMessage) => {
  // Check if password is empty first
  if (!value || value.trim() === '') {
    return formatMessage(messages['empty.password.field.error']);
  }
  // Check password conditions validators
  const conditions = convertConditionsWithMessages(value, '', formatMessage);
  for (let i = 0; i < conditions.length; i++) {
    if (!conditions[i].condition) {
      return conditions[i].message;
    }
  }
  return '';
};

/**
 * Validates the confirm password field value
 * @param value - confirm_password field's value
 * @param newPassword - new password value to compare against
 * @param formatMessage - intl formatMessage function
 * @returns {string} - error message or empty string if valid
 */
export const validateConfirmPasswordField = (value, newPassword, formatMessage) => {
  if (!value) { 
    return formatMessage(messages['empty.password.field.error']);
  }
  if (value !== newPassword) {
    return formatMessage(messages['passwords.do.not.match']);
  }
  return '';
};
